/**
 * FormsInboxPage.tsx
 *
 * Patient-form-submission inbox at /surveys/all. Functional parity with
 * legacy `message_center-test.php?v=survey`:
 *
 *   - Status tabs: Unread / Read, plus Incomplete when the account has
 *     e-form partial saves on (sidebar-style in the toolbar). Unread
 *     shows a red badge with how many unread rows match the filters.
 *   - Sidebar filters: Search, Date range, E-Form (with +/- semantics).
 *     Filter selections persist into v4 `users_filters_lists` so the
 *     legacy UI and V5 share state per user.
 *   - Sortable columns: Date, From, Subject.
 *   - Bulk Mark Read on the Unread tab when rows are checked.
 *   - Row click opens FormInboxResponseModal (Complete / Delete /
 *     Send to Notes; auto mark-read on open).
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { usePageContext } from '@/hooks/usePageContext';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import type {
  FilterConfig,
  FilterNegations,
  FilterOption,
  FilterValue,
} from '@/types/filters.types';
import { isDateRange, isSelect } from '@/types/filters.types';
import { FormInboxResponseModal } from './FormInboxResponseModal';
import {
  bulkMarkFormsInboxRead,
  getFormsInboxFilterOptions,
  getFormsInboxUnreadCount,
  getSavedFormsInboxFilters,
  listFormsInbox,
  saveFormsInboxFilter,
  type FormsInboxFilterParams,
  type FormsInboxListParams,
  type FormsInboxRow,
  type FormsInboxSavedFilters,
  type FormsInboxSortBy,
  type FormsInboxStatus,
} from '@/services/formsInbox.service';
import { toLocaleDateStringInTz, toLocaleTimeStringInTz } from '@/lib/facilityTimezone';

// ─── Constants ────────────────────────────────────────────────────────────────

const PER_PAGE = 100;

const STATUS_TABS: { key: FormsInboxStatus; label: string }[] = [
  { key: 'unread', label: 'Unread' },
  { key: 'read', label: 'Read' },
];

// Only accounts with e-form partial saves on keep unfinished forms.
const INCOMPLETE_TAB: { key: FormsInboxStatus; label: string } = { key: 'incomplete', label: 'Incomplete' };

const FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search' },
  { key: 'daterange', label: 'Date', type: 'daterange' },
  { key: 'eform', label: 'E-Form', type: 'select', multi: true, searchable: true },
];

const SORT_KEY_TO_PARAM: Record<string, FormsInboxSortBy> = {
  date: 'date',
  fromName: 'name',
  subject: 'subject',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDateLine(value: string | null | undefined, tz?: string): string {
  if (!value) return '\u2014';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return toLocaleDateStringInTz(parsed, 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(tz ? { timeZone: tz } : {}),
  });
}

function formatTimeLine(value: string | null | undefined, tz?: string): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return toLocaleTimeStringInTz(parsed, 'en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    ...(tz ? { timeZone: tz } : {}),
  });
}

function emptySaved(): FormsInboxSavedFilters {
  return {
    eform: { values: [], plus: true },
    doctor: { values: [], plus: true },
    coordinator: { values: [], plus: true },
    daterange: { from: '', to: '', preset: '' },
  };
}

function valuesFromSaved(
  saved: FormsInboxSavedFilters,
  search: string,
): Record<string, FilterValue> {
  return {
    search: search ? [search] : [],
    daterange: { from: saved.daterange.from, to: saved.daterange.to },
    eform: saved.eform.values.map(String),
  };
}

function negationsFromSaved(saved: FormsInboxSavedFilters): FilterNegations {
  return {
    eform: !saved.eform.plus,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function FormsInboxPage() {
  const queryClient = useQueryClient();
  const updateAIContext = usePageContext('Forms Inbox');
  const userTz = useUserTimezone();

  const [status, setStatus] = useState<FormsInboxStatus>('unread');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<string>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [openResponseId, setOpenResponseId] = useState<number | null>(null);

  // ── Server-persisted sidebar filters (shared with legacy UI) ──────────────

  const { data: savedFilters, isFetched: savedFiltersLoaded } = useQuery({
    queryKey: ['forms-inbox-saved-filters'],
    queryFn: getSavedFormsInboxFilters,
    staleTime: 10_000,
  });

  const { data: filterOptions } = useQuery({
    queryKey: ['forms-inbox-filter-options'],
    queryFn: getFormsInboxFilterOptions,
    staleTime: 5 * 60_000,
  });

  const statusTabs = filterOptions?.partialSaves ? [...STATUS_TABS, INCOMPLETE_TAB] : STATUS_TABS;

  const effectiveSaved = savedFilters ?? emptySaved();
  const filterValues = useMemo(
    () => valuesFromSaved(effectiveSaved, search),
    [effectiveSaved, search],
  );
  const filterNegations = useMemo(() => negationsFromSaved(effectiveSaved), [effectiveSaved]);

  const sidebarOptions = useMemo<Record<string, FilterOption[]>>(() => ({
    eform: (filterOptions?.eforms ?? []).map((o) => ({ value: String(o.value), label: o.label })),
  }), [filterOptions]);

  const saveFilterMutation = useMutation({
    mutationFn: saveFormsInboxFilter,
    onSuccess: (next) => {
      queryClient.setQueryData(['forms-inbox-saved-filters'], next);
      queryClient.invalidateQueries({ queryKey: ['forms-inbox'] });
      queryClient.invalidateQueries({ queryKey: ['forms-inbox-unread-count'] });
    },
  });

  const handleFilterChange = (key: string, value: FilterValue) => {
    if (key === 'search') {
      if (isSelect(value)) {
        setSearch(value[0] ?? '');
        setPage(1);
      }
      return;
    }
    if (key === 'daterange') {
      if (isDateRange(value)) {
        saveFilterMutation.mutate({
          type: 'daterange',
          dateFrom: value.from,
          dateTo: value.to,
          preset: '',
        });
      }
      return;
    }
    if (key === 'eform') {
      if (isSelect(value)) {
        const numericValues = value
          .map((v) => Number(v))
          .filter((n) => Number.isFinite(n));
        const plus = !(filterNegations[key] ?? false);
        saveFilterMutation.mutate({
          type: key,
          values: numericValues,
          plus,
        });
      }
    }
  };

  const handleNegateChange = (key: string, negate: boolean) => {
    if (key !== 'eform') return;
    const current = effectiveSaved[key];
    saveFilterMutation.mutate({
      type: key,
      values: current.values,
      plus: !negate,
    });
  };

  const handleClearFilter = (key: string) => {
    if (key === 'search') {
      setSearch('');
      setPage(1);
      return;
    }
    if (key === 'daterange') {
      saveFilterMutation.mutate({ type: 'daterange', dateFrom: '', dateTo: '', preset: '' });
      return;
    }
    if (key === 'eform') {
      saveFilterMutation.mutate({ type: key, values: [], plus: true });
    }
  };

  // ── List query ────────────────────────────────────────────────────────────

  const filterParams: FormsInboxFilterParams = useMemo(() => {
    const surveys = effectiveSaved.eform.values;
    return {
      search: search || undefined,
      dateFrom: effectiveSaved.daterange.from || undefined,
      dateTo: effectiveSaved.daterange.to || undefined,
      surveys: surveys.length ? surveys : undefined,
      surveysExclude: surveys.length && !effectiveSaved.eform.plus ? true : undefined,
    };
  }, [search, effectiveSaved]);

  const params: FormsInboxListParams = useMemo(() => ({
    ...filterParams,
    status,
    page,
    perPage: PER_PAGE,
    sortBy: SORT_KEY_TO_PARAM[sortKey] ?? 'date',
    sortDir,
  }), [filterParams, status, page, sortKey, sortDir]);

  const { data: listData, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['forms-inbox', params],
    queryFn: () => listFormsInbox(params),
    staleTime: 10_000,
  });

  const rows = listData?.data ?? [];
  const total = (listData?.meta?.total as number | undefined) ?? 0;

  // Reset selection / page when status or filters change.
  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [status, params.search, params.dateFrom, params.dateTo, params.surveys]);

  // ── Unread badge ──────────────────────────────────────────────────────────

  // What the Unread tab lists under the same filters (SCRUM-2419). It waits
  // for the saved filters so it never flashes the unfiltered total.
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['forms-inbox-unread-count', filterParams],
    queryFn: () => getFormsInboxUnreadCount(filterParams),
    enabled: savedFiltersLoaded,
    staleTime: 10_000,
  });

  useEffect(() => {
    updateAIContext({
      filters: `Status: ${status}${search ? `, Search: "${search}"` : ''}`,
      dataSummary: `${total} form submission(s) match the current filters, ${unreadCount} of them unread`,
    });
  }, [updateAIContext, status, search, total, unreadCount]);

  // ── Bulk mark-read ────────────────────────────────────────────────────────

  const bulkMarkReadMutation = useMutation({
    mutationFn: (ids: number[]) => bulkMarkFormsInboxRead(ids),
    onSuccess: () => {
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['forms-inbox'] });
      queryClient.invalidateQueries({ queryKey: ['forms-inbox-unread-count'] });
    },
  });

  const handleBulkMarkRead = () => {
    const ids = Array.from(selectedIds).map((id) => Number(id)).filter((n) => Number.isFinite(n));
    if (ids.length === 0) return;
    bulkMarkReadMutation.mutate(ids);
  };

  // ── DataTable wiring ──────────────────────────────────────────────────────

  const columns = useMemo<DataTableColumn<FormsInboxRow>[]>(() => [
    {
      key: 'date',
      header: 'Date',
      sortable: true,
      width: 'w-44',
      render: (row) => (
        <div>
          <div className="text-sm text-slate-800">{formatDateLine(row.date, userTz)}</div>
          {row.date && (
            <div className="text-xs text-slate-500">{formatTimeLine(row.date, userTz)}</div>
          )}
        </div>
      ),
    },
    {
      key: 'fromName',
      header: 'From',
      sortable: true,
      render: (row) => (
        <span className="font-medium text-slate-800">{row.fromName || '\u2014'}</span>
      ),
    },
    {
      key: 'subject',
      header: 'Form',
      sortable: true,
      render: (row) => <span className="text-slate-700">{row.subject || '\u2014'}</span>,
    },
  ], [userTz]);

  // Selection only on Unread tab (matches legacy `showButtons` logic where
  // the Mark Read bulk action is only visible when listType === 'newInbox').
  const selectable = status === 'unread';

  const handleSortChange = (key: string, dir: 'asc' | 'desc') => {
    setSortKey(key);
    setSortDir(dir);
  };

  return (
    <>
      <ListPageLayout
        filterSidebar={(
          <FilterSidebar
            filters={FILTER_CONFIGS}
            values={filterValues}
            options={sidebarOptions}
            negations={filterNegations}
            onChange={handleFilterChange}
            onNegateChange={handleNegateChange}
            onClear={handleClearFilter}
            searchCommitMode="blur-or-enter"
            storageKey="forms-inbox"
            showAIAssistant={false}
          />
        )}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 space-y-4 p-6 pb-0">
            <PageHeader variant="compact" title="Forms" />

            <div className="flex flex-wrap items-center gap-1 border-b border-slate-200">
              {statusTabs.map((tab) => {
                const isActive = status === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setStatus(tab.key)}
                    className={`inline-flex items-center gap-1.5 rounded-t-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      isActive
                        ? 'border-b-2 border-primary text-primary bg-primary/10'
                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {tab.label}
                    {tab.key === 'unread' && unreadCount > 0 && (
                      <span className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold leading-none ${
                        isActive ? 'bg-primary text-white' : 'bg-red-500 text-white'
                      }`}>
                        {unreadCount}
                      </span>
                    )}
                  </button>
                );
              })}
              <div className="ml-auto flex items-center gap-2">
                {selectable && selectedIds.size > 0 && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleBulkMarkRead}
                    disabled={bulkMarkReadMutation.isPending}
                  >
                    {bulkMarkReadMutation.isPending
                      ? 'Marking...'
                      : `Mark ${selectedIds.size} Read`}
                  </Button>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => refetch()}
                  disabled={isFetching}
                  leftIcon={<RefreshCw className={isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />}
                >
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            <DataTable<FormsInboxRow>
              data={rows}
              columns={columns}
              keyField="id"
              loading={isLoading}
              page={page}
              pageSize={PER_PAGE}
              total={total}
              onPageChange={(p) => setPage(p)}
              onRowClick={(row) => setOpenResponseId(row.id)}
              selectable={selectable}
              selectedIds={selectable ? selectedIds : undefined}
              onSelectionChange={selectable ? setSelectedIds : undefined}
              manualSort
              sortKey={sortKey}
              sortDir={sortDir}
              onSortChange={handleSortChange}
              emptyTitle="No forms found"
              emptyDescription={
                status === 'unread'
                  ? 'No unread submissions match the current filters.'
                  : status === 'read'
                  ? 'No read submissions match the current filters.'
                  : 'No incomplete submissions match the current filters.'
              }
            />
          </div>
        </div>
      </ListPageLayout>

      <FormInboxResponseModal
        responseId={openResponseId}
        open={openResponseId !== null}
        onClose={() => setOpenResponseId(null)}
        status={status}
      />
    </>
  );
}

export default FormsInboxPage;
